// parse-web.ts — the WEB half of on-device statement parsing: a faithful 1:1 JS port of
// src-tauri/src/parse.rs (CSV path) + categorize.rs, so the web surface can do the app's core
// job in the browser. PDF stays NATIVE-ONLY (lopdf text extraction has no sane browser twin;
// the import sheet says so on web). Everything here is local — file content never leaves the
// browser; the result is handed back to the UI to confirm + store, exactly like the Rust side.
//
// Amount convention (identical to parse.rs): signed MINOR UNITS (pennies/cents).
// Money OUT (debit) is NEGATIVE; money IN (credit) is POSITIVE.
//
// ⚠️ Keep in sync with the Rust: parse.rs (parse_money_minor / parse_date / clean_merchant /
// split_csv_line / parse_csv), categorize.rs (RULES/categorize), db.rs (norm_desc, dedupe key).

import type { ParsedTxn } from "./store";

// ── Money parsing (parse.rs::parse_money_minor) ──────────────────────────────
export function parseMoneyMinor(raw: string): number | null {
  let s = raw.trim();
  if (!s) return null;
  let negative = false;

  if (s.startsWith("(") && s.endsWith(")")) {
    negative = true;
    s = s.slice(1, -1);
  }
  const upper = s.toUpperCase();
  if (upper.endsWith("DR")) {
    negative = true;
    s = s.slice(0, -2);
  } else if (upper.endsWith("CR")) {
    s = s.slice(0, -2);
  }

  let cleaned = [...s].filter((c) => /[0-9.,+-]/.test(c)).join("");
  if (cleaned.startsWith("-")) {
    negative = true;
    cleaned = cleaned.slice(1);
  }
  if (cleaned.startsWith("+")) cleaned = cleaned.slice(1);
  if (!cleaned) return null;

  const lastDot = cleaned.lastIndexOf(".");
  const lastComma = cleaned.lastIndexOf(",");
  let normalized: string;
  if (lastDot >= 0 && lastComma >= 0) {
    normalized = lastDot > lastComma
      ? cleaned.replaceAll(",", "")
      : cleaned.replaceAll(".", "").replaceAll(",", ".");
  } else if (lastComma >= 0) {
    const frac = cleaned.length - lastComma - 1;
    normalized = frac === 2 ? cleaned.replaceAll(",", ".") : cleaned.replaceAll(",", "");
  } else {
    normalized = cleaned;
  }

  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  const minor = Math.round(Math.abs(value) * 100);
  return negative ? -minor : minor;
}

// ── Date parsing (parse.rs::parse_date) ──────────────────────────────────────
const MONTHS: [string, number][] = [
  ["jan", 1], ["feb", 2], ["mar", 3], ["apr", 4], ["may", 5], ["jun", 6],
  ["jul", 7], ["aug", 8], ["sep", 9], ["oct", 10], ["nov", 11], ["dec", 12],
];

function validYmd(y: number, m: number, d: number): string | null {
  if (m < 1 || m > 12 || d < 1) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function tryIso(s: string): string | null {
  const head = s.slice(0, 10);
  const parts = head.split(/[-/]/);
  if (parts.length === 3 && parts[0].length === 4) {
    const y = Number(parts[0]), m = Number(parts[1]), d = Number(parts[2]);
    if (Number.isInteger(y) && Number.isInteger(m) && Number.isInteger(d)) return validYmd(y, m, d);
  }
  return null;
}

function tryNamedMonth(s: string): string | null {
  const lower = s.toLowerCase();
  const month = MONTHS.find(([name]) => lower.includes(name))?.[1];
  if (!month) return null;
  const nums = lower.split(/[^0-9]/).filter(Boolean).map(Number);
  if (!nums.length) return null;
  const day = nums.find((n) => n >= 1 && n <= 31);
  if (day == null) return null;
  let year = nums.find((n) => n >= 1900 || (n >= 0 && n <= 99 && n !== day));
  if (year == null) year = new Date().getFullYear();
  if (year < 100) year += 2000;
  return validYmd(year, month, day);
}

function tryNumeric(s: string, preferMdy: boolean): string | null {
  let head = "";
  for (const c of s) {
    if (/[0-9/.-]/.test(c)) head += c;
    else break;
  }
  const parts = head.split(/[/.-]/).filter(Boolean);
  if (parts.length !== 3) return null;
  let a = Number(parts[0]);
  let b = Number(parts[1]);
  let y = Number(parts[2]);
  if (![a, b, y].every(Number.isInteger)) return null;
  if (y < 100) y += 2000;
  let day: number, month: number;
  if (a > 12) { day = a; month = b; }
  else if (b > 12) { day = b; month = a; }
  else if (preferMdy) { day = b; month = a; }
  else { day = a; month = b; }
  return validYmd(y, month, day);
}

export function parseDate(raw: string, preferMdy = false): string | null {
  const s = raw.trim();
  if (!s) return null;
  return tryIso(s) ?? tryNamedMonth(s) ?? tryNumeric(s, preferMdy);
}

// ── Merchant cleanup (parse.rs::clean_merchant) ──────────────────────────────
export function cleanMerchant(raw: string): string {
  let s = raw.trim();
  for (const pre of ["card purchase ", "pos ", "debit card ", "visa ", "mastercard ", "contactless "]) {
    if (s.toLowerCase().startsWith(pre)) s = s.slice(pre.length);
  }
  for (const marker of ["  ", " ref ", " ref:", " on ", " card no", "*"]) {
    const idx = s.toLowerCase().indexOf(marker);
    if (idx > 3) s = s.slice(0, idx);
  }
  const kept: string[] = [];
  for (const w of s.split(/\s+/).filter(Boolean)) {
    const digitRatio = [...w].filter((c) => /[0-9]/.test(c)).length / Math.max(w.length, 1);
    if (w.length >= 6 && digitRatio > 0.6) continue; // looks like a reference number
    kept.push(w);
  }
  let out = kept.join(" ").trim();
  if (!out) out = raw.trim();
  if (out.length > 48) out = out.slice(0, 48).trimEnd();
  return out;
}

// ── CSV parsing (parse.rs::split_csv_line / header_index / parse_csv) ────────
export function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; }
        else inQuotes = false;
      } else cur += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") { out.push(cur.trim()); cur = ""; }
    else cur += c;
  }
  out.push(cur.trim());
  return out;
}

function headerIndex(headers: string[], names: string[]): number | null {
  for (let i = 0; i < headers.length; i++) {
    const hl = headers[i].toLowerCase();
    for (const n of names) if (hl === n || hl.includes(n)) return i;
  }
  return null;
}

/** Parse a CSV statement — auto-detects header row + date/description/amount (or debit+credit)
 *  columns by name, exactly like parse.rs. Throws Error with the same customer-facing messages. */
export function parseCsv(content: string, defaultCurrency: string): ParsedTxn[] {
  const lines = content.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) throw new Error("empty file");

  let headerRow = 0;
  let headers: string[] = [];
  let found = false;
  for (let i = 0; i < Math.min(lines.length, 15); i++) {
    const cols = splitCsvLine(lines[i]);
    const joined = cols.join(" ").toLowerCase();
    const hasDate = joined.includes("date");
    const hasAmt = joined.includes("amount") || joined.includes("debit") || joined.includes("credit")
      || joined.includes("value") || joined.includes("paid");
    if (hasDate && hasAmt) { headerRow = i; headers = cols; found = true; break; }
  }
  if (!found) throw new Error("could not find a header row with date + amount columns — this CSV layout isn't recognised yet");

  const dateI = headerIndex(headers, ["date", "transaction date", "posted"]);
  if (dateI == null) throw new Error("no date column");
  const descI = headerIndex(headers, ["description", "details", "merchant", "payee", "narrative", "reference", "memo", "name"]);
  const amountI = headerIndex(headers, ["amount", "value", "money in/out"]);
  const debitI = headerIndex(headers, ["debit", "paid out", "money out", "withdrawal", "outgoing"]);
  const creditI = headerIndex(headers, ["credit", "paid in", "money in", "deposit", "incoming"]);
  const currencyI = headerIndex(headers, ["currency", "ccy"]);
  if (amountI == null && debitI == null && creditI == null) throw new Error("no amount / debit / credit column found");

  const preferMdy = false; // UK/EU default; day>12 heuristic auto-corrects (same as Rust)
  const out: ParsedTxn[] = [];
  for (const line of lines.slice(headerRow + 1)) {
    const cols = splitCsvLine(line);
    if (cols.length <= dateI) continue;
    const postedOn = parseDate(cols[dateI], preferMdy);
    if (!postedOn) continue; // skip non-data rows (totals/footers)
    const rawDescription = (descI != null ? cols[descI] : "") ?? "";

    let amount: number | null;
    if (amountI != null) {
      amount = cols[amountI] != null ? parseMoneyMinor(cols[amountI]) : null;
    } else {
      const debit = debitI != null && cols[debitI] != null ? parseMoneyMinor(cols[debitI]) : null;
      const credit = creditI != null && cols[creditI] != null ? parseMoneyMinor(cols[creditI]) : null;
      amount = debit != null && debit !== 0 ? -Math.abs(debit)
        : credit != null && credit !== 0 ? Math.abs(credit)
        : null;
    }
    if (amount == null || amount === 0) continue;

    let currency = defaultCurrency;
    if (currencyI != null && cols[currencyI]) {
      const c = cols[currencyI].trim().toUpperCase();
      if (c.length === 3) currency = c;
    }

    out.push({ posted_on: postedOn, merchant: cleanMerchant(rawDescription), raw_description: rawDescription, amount, currency });
  }

  if (!out.length) throw new Error("no transactions parsed from this CSV");
  return out;
}

// ── Categorization (categorize.rs — keep the RULES list byte-identical) ──────
const RULES: [string, string[]][] = [
  ["subscriptions", ["netflix", "spotify", "disney+", "disney plus", "amazon prime", "prime video",
    "apple.com/bill", "apple services", "youtube premium", "icloud", "dropbox",
    "audible", "patreon", "substack", "linkedin", "adobe", "microsoft 365",
    "office 365", "github", "openai", "anthropic", "notion", "canva"]],
  ["groceries", ["tesco", "sainsbury", "asda", "morrisons", "aldi", "lidl", "waitrose",
    "co-op", "co op", "iceland", "ocado", "marks & spencer food", "m&s food",
    "whole foods", "trader joe", "kroger", "safeway", "walmart grocery",
    "carrefour", "rewe", "edeka", "spar", "supermarket", "grocery"]],
  ["dining", ["mcdonald", "kfc", "burger king", "subway", "greggs", "nando", "pizza",
    "domino", "starbucks", "costa", "cafe", "coffee", "restaurant", "deliveroo",
    "uber eats", "ubereats", "just eat", "justeat", "doordash", "grubhub",
    "wagamama", "pret a manger", "pret", "bar", "pub", "diner", "takeaway",
    "five guys", "chipotle"]],
  ["fuel", ["shell", "bp ", "esso", "texaco", "gulf", "petrol", "fuel", "gas station",
    "exxon", "chevron", "petronas", "total energies", "totalenergies", "petro-canada"]],
  ["transport", ["uber", "lyft", "bolt", "tfl", "transport for london", "train", "rail",
    "trainline", "national rail", "bus", "metro", "tube", "subway transit",
    "parking", "ncp", "toll", "taxi", "cab", "scooter", "lime", "citymapper",
    "oyster", "amtrak"]],
  ["fees", ["overdraft", "interest charge", "service charge", "atm fee", "card fee",
    "annual fee", "late fee", "foreign transaction", "non-sterling", "fx fee",
    "bank charge", "maintenance fee"]],
  ["cash", ["atm", "cash withdrawal", "cash machine", "withdrawal", "cashpoint"]],
  ["utilities", ["british gas", "edf", "eon", "e.on", "octopus energy", "ovo energy",
    "scottish power", "thames water", "anglian water", "severn trent",
    "bt ", "sky", "virgin media", "vodafone", "o2", "ee ", "three ",
    "talktalk", "comcast", "xfinity", "verizon", "at&t", "council tax",
    "electric", "water bill", "broadband", "utility"]],
  ["rent_mortgage", ["rent", "landlord", "mortgage", "letting", "property management", "halifax mortgage"]],
  ["health", ["pharmacy", "boots", "superdrug", "nhs", "dental", "dentist", "doctor",
    "clinic", "hospital", "optician", "specsavers", "gym", "puregym", "the gym",
    "fitness", "wellness", "cvs", "walgreens"]],
  ["entertainment", ["cinema", "odeon", "vue", "cineworld", "theatre", "ticketmaster", "eventbrite",
    "steam", "playstation", "xbox", "nintendo", "concert", "festival", "bowling"]],
  ["travel", ["ryanair", "easyjet", "british airways", "ba.com", "airline", "airways",
    "booking.com", "airbnb", "hotel", "expedia", "hostel", "trip.com",
    "lufthansa", "klm", "emirates", "tui", "jet2", "hertz", "avis", "enterprise rent"]],
  ["shopping", ["amazon", "ebay", "argos", "john lewis", "ikea", "next ", "asos", "zara",
    "h&m", "primark", "apple store", "currys", "best buy", "target", "etsy",
    "aliexpress", "shein", "decathlon", "sports direct", "screwfix", "b&q",
    "wickes", "homebase", "store", "shop"]],
  ["income", ["salary", "payroll", "wages", "hmrc", "tax refund", "dividend", "interest paid",
    "refund", "bacs credit", "faster payment received", "pension"]],
  ["transfers", ["transfer", "transfer to", "transfer from", "standing order", "direct debit",
    "bank transfer", "payment to", "savings", "isa", "venmo", "zelle", "paypal",
    "wise", "revolut", "monzo pot", "to savings"]],
];

export function categorize(merchant: string, rawDescription: string): string {
  const hay = `${merchant.toLowerCase()} ${rawDescription.toLowerCase()}`;
  for (const [cat, keywords] of RULES) {
    for (const kw of keywords) if (hay.includes(kw)) return cat;
  }
  return "uncategorized";
}

// ── Dedupe key (db.rs::norm_desc + import_txns) ──────────────────────────────
export function normDesc(s: string): string {
  return s.toLowerCase().split(/\s+/).filter(Boolean).join(" ");
}

export function dedupeKey(accountId: string, t: ParsedTxn): string {
  return `${accountId}|${t.posted_on}|${t.amount}|${normDesc(t.raw_description)}`;
}
